import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import zipcodes from 'zipcodes';
import '../Stylesheets/RealEstateEntries.css';

const SOLD_STATUSES = new Set(['sold', 'closed']);

function RealEstateEntries({ properties, pageTitle }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    zipCode: '',
    distance: '10',
    bedrooms: '',
    bathrooms: '',
    acreage: '',
    priceMin: '',
    priceMax: '',
    livingSqFt: '',
    totalSqFt: '',
    yearBuilt: '',
    zoning: '',
  });
  const [statusView, setStatusView] = useState('active');

  const propertiesPerPage = 12;
  const navigate = useNavigate();

  // Calculate distance between two ZIP codes
  const getDistance = (zip1, zip2) => {
    if (!zip1 || !zip2) return Infinity;
 
    const location1 = zipcodes.lookup(zip1);
    const location2 = zipcodes.lookup(zip2);

    if (!location1 || !location2) return Infinity;

    const R = 3958.8; // Radius of Earth in miles
    const lat1 = (location1.latitude * Math.PI) / 180;
    const lon1 = (location1.longitude * Math.PI) / 180;
    const lat2 = (location2.latitude * Math.PI) / 180;
    const lon2 = (location2.longitude * Math.PI) / 180;

    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in miles
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  // Filter listings based on search term and filters
  const filteredProperties = properties.filter((property) => {
    const {
      streetAddress,
      parcelNumber,
      listPrice,
      bedrooms,
      bathrooms,
      zipCode,
      acreage,
      livingSqFt,
      totalSqFt,
      yearBuilt,
      zoning,
      propertyId,
      user_id,
    } = property;

    const price = Number((listPrice || "").toString().replace(/[^0-9.-]+/g, ""));
    const listingStatus = (property.listingStatus || property.status || '').toLowerCase();
    const isSoldListing = SOLD_STATUSES.has(listingStatus);
    if (statusView === 'active' && isSoldListing) {
      return false;
    }
    if (statusView === 'sold' && !isSoldListing) {
      return false;
    }


     const matchesSearchTerm =
      (streetAddress ? streetAddress.toLowerCase() : "").includes(searchTerm.toLowerCase()) ||
      (propertyId && propertyId.toString().includes(searchTerm)); // Updated to use propertyId


    const matchesFilters =
      (filters.zipCode === '' ||
        (filters.distance === '' || getDistance(filters.zipCode, zipCode) <= Number(filters.distance))) &&
      (filters.bedrooms === '' || bedrooms >= Number(filters.bedrooms)) &&
      (filters.bathrooms === '' || bathrooms >= Number(filters.bathrooms)) &&
      (filters.acreage === '' || acreage >= Number(filters.acreage)) &&
      (filters.priceMin === '' || price >= Number(filters.priceMin)) &&
      (filters.priceMax === '' || price <= Number(filters.priceMax)) &&
      (filters.livingSqFt === '' || livingSqFt >= Number(filters.livingSqFt)) &&
      (filters.totalSqFt === '' || totalSqFt >= Number(filters.totalSqFt)) &&
      (filters.yearBuilt === '' || yearBuilt === Number(filters.yearBuilt)) &&
      (filters.zoning === '' || zoning.toLowerCase().includes(filters.zoning.toLowerCase()));

    return matchesSearchTerm && matchesFilters;
  });
  
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handlePropertyClick = (propertyId) => {

    if (!propertyId) {
      console.warn("Property ID is undefined or invalid. Navigation aborted.");
      return;
    }

    // Navigate to the property details page
    navigate(`/property/${propertyId}`);
  };

  return (
    <div className="listings-wrapper">
      <div className="container py-3">
        <div className="listings-header">
          <h1>{pageTitle}</h1>
          <div className="listings-actions">
            <div className="filter-input">
              <label>Listing View</label>
              <select
                className="form-select"
                value={statusView}
                onChange={(e) => {
                  setStatusView(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="active">Active & Pending</option>
                <option value="sold">Sold Only</option>
                <option value="all">All Listings</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card shadow-sm listings-search-card mb-4">
          <div className="card-body">
            <div className="row g-1">
              <div className="col-md-6 filter-input">
                <label>Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by address, MLS number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-3 filter-input">
                <label>ZIP Code</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter ZIP Code"
                  name="zipCode"
                  value={filters.zipCode}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-3 filter-input">
                <label>Distance (miles)</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Distance"
                  name="distance"
                  value={filters.distance}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="row g-1 mt-1">
              {[
                { name: 'bedrooms', placeholder: 'Bedrooms', type: 'number' },
                { name: 'bathrooms', placeholder: 'Bathrooms', type: 'number' },
                { name: 'acreage', placeholder: 'Acreage', type: 'number' },
                { name: 'priceMin', placeholder: 'Min Price', type: 'number' },
                { name: 'priceMax', placeholder: 'Max Price', type: 'number' },
                { name: 'livingSqFt', placeholder: 'Living SqFt', type: 'number' },
                { name: 'totalSqFt', placeholder: 'Total SqFt', type: 'number' },
                { name: 'yearBuilt', placeholder: 'Year Built', type: 'number' },
                { name: 'zoning', placeholder: 'Zoning' },
              ].map((filter, index) => (
                <div className="col-6 col-md-4 col-lg-3 filter-input" key={index}>
                  <label>{filter.placeholder}</label>
                  <input
                    type={filter.type || 'text'}
                    className="form-control form-control-sm"
                    name={filter.name}
                    value={filters[filter.name]}
                    onChange={handleFilterChange}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 listings-card-grid">
          {currentProperties.map((property) => (
            <div
              className="col"
              key={property.propertyId}
              onClick={() => handlePropertyClick(property.propertyId)}
            >
              <div className="card listing-card h-100 shadow-sm" role="button">
                <img
                  src={property.imageUrl || "https://via.placeholder.com/600x400?text=Listing"}
                  alt={property.streetAddress || "Property"}
                  onError={(e) => (e.target.src = "https://via.placeholder.com/600x400?text=Listing")}
                />
                <div className="card-body">
                  {property.streetAddress && (
                    <h5 className="card-title mb-1">{property.streetAddress}</h5>
                  )}
                  {(property.city || property.state || property.zipCode) && (
                    <p className="text-muted mb-2">
                      {[property.city, property.state, property.zipCode]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}
                  {property.listPrice && (
                    <div className="listing-price mb-3">
                      ${property.listPrice}
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-3 listing-meta">
                    {(property.bedrooms || property.bathrooms) && (
                      <div>
                        Beds / Baths <br />
                        <strong>
                          {property.bedrooms ?? 0} / {property.bathrooms ?? 0}
                        </strong>
                      </div>
                    )}
                    {property.livingSqFt && (
                      <div>
                        Living SqFt <br />
                        <strong>{property.livingSqFt}</strong>
                      </div>
                    )}
                    {property.acreage && (
                      <div>
                        Acreage <br />
                        <strong>{property.acreage}</strong>
                      </div>
                    )}
                    {property.buyerFee && (
                      <div>
                        Buyer's Side <br />
                        <strong>{property.buyerFee}</strong>
                      </div>
                    )}
                    {property.listingStatus && (
                      <div>
                        Status <br />
                        <strong>{property.listingStatus}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <nav aria-label="Listings pagination" className="mt-4">
          <ul className="pagination justify-content-center">
            {[...Array(totalPages)].map((_, index) => (
              <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => paginate(index + 1)}>
                  {index + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}

export default RealEstateEntries;
